import {
  type Theme,
  type UserProfile,
  ThemeDefaultEcommerceBookingOptionEnum,
  ThemeHourlyTitleConfigurationEnum,
  ThemeMonthlyTitleConfigurationEnum,
  ThemePermitBookingStartDateEnum,
  ThemeReservationHomeSearchScreenEnum,
} from '@parkingbase/ecommerce-api-demo';

import {
  addDays,
  addMinutes,
  addMonths,
  isAfter,
  isBefore,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { ReservationMode } from '@/components/reserve/search-bar/SearchBar.types';
import type {
  ProductDisplayElement,
  RateCardLabelDimension,
  RateCardTag,
  ReservationSearchLayout,
} from '@/types/reserve.types';
import { logger } from './logger';
import { parseFlexibleDate, PERMIT_DATE_FORMATS, roundToNext15Minutes } from './date-time-utils';
import { generateDarkTheme } from './dark-mode-utils';
import { countryToAlphaCode } from './country-utils';

const DEFAULT_FUTURE_BOOKING_DAYS = 365;
const DEFAULT_DURATION_MINUTES = 180;

const DEFAULT_SEARCHABLE_COUNTRIES = ['us', 'ca'];

const DEFAULT_LOGO = '/images/logo.png';

/**
 * Resolves the company-specific theme for a converge website.
 * On a converge website, each company has its own theme in `companyThemes`.
 * Falls back to the default website theme when the company theme is not found
 * or the site is not a converge website.
 */
export function resolveCompanyTheme(
  companyId: string | undefined,
  companyThemes: Record<string, Theme>,
  defaultTheme: Theme | null
): Theme | null {
  return (companyId && companyThemes[companyId]) || defaultTheme;
}

/**
 * Whether a stored logo value is ALREADY something an `<img>` can load, as opposed to a bare
 * base64 payload that needs a data-URI wrapper.
 *
 * ## Invariants:
 * - Only `data:` and `http(s)://` count, and a leading `/` deliberately does NOT. Why: `/` is in
 *   the base64 alphabet, and base64 of any JPEG starts `/9j/` (the bytes `0xFF 0xD8`) — treating
 *   a leading slash as a path would leave every base64 JPEG logo unwrapped and broken. A colon
 *   cannot appear in base64 at all, so the two accepted prefixes are unambiguous.
 * - Consequence, accepted knowingly: an app-absolute path like `/images/x.png` is NOT recognised
 *   and would be base64-wrapped. No caller stores one — these values come from the API as base64
 *   or as an uploaded-file URL, and the local default asset is returned directly rather than
 *   passed through here.
 */
function isRenderableImageSrc(value: string): boolean {
  return value.startsWith('data:') || value.startsWith('http://') || value.startsWith('https://');
}

/** Wraps a bare base64 payload in a data URI; passes an already-usable src through untouched. */
function toImageSrc(value: string): string {
  return isRenderableImageSrc(value) ? value : `data:image/png;base64,${value}`;
}

/**
 * Resolves a theme logo value to a usable `<img>` src, falling back to the default static logo.
 *
 * ## Invariants:
 * - A value that is already a usable src — a `data:` URI or an `http(s)` URL — passes through
 *   UNTOUCHED; only a bare payload gets the base64 wrapper. Why: the Back
 *   Office stores some logo fields as base64 and others as an uploaded-file URL, and the type is
 *   a plain `string` either way (`DiscountProgram.logo`), so the shape can only be read off the
 *   value. Prefixing a URL yields `data:image/png;base64,https://…`, which is not a broken
 *   image in some edge case — it is a broken image every time that field holds a URL.
 * - Blank still resolves to the default asset rather than to an empty src. Why: an empty `src`
 *   makes the browser re-request the current page as an image.
 */
export function resolveLogoSrc(logo: string | undefined | null): string {
  const value = logo?.trim() ?? '';
  return value.length > 0 ? toImageSrc(value) : DEFAULT_LOGO;
}

/**
 * Resolves the logo to render ON a brand-coloured surface (e.g. the branded loader, which sits
 * on `--color-primary`).
 *
 * ## Invariants:
 * - `logoWhite` wins over `logo`. Why: the standard logo is tuned for a white surface and can
 *   disappear against the primary fill; the white variant is the asset the tenant supplied
 *   specifically for dark backgrounds.
 * - Returns `undefined` — never the default static asset — when the tenant has set neither.
 *   Why: callers render this on a brand fill where the generic ParkingBase mark would be both
 *   off-brand and, for a white-label tenant, actively wrong. The loader is designed to compose
 *   without a logo, and a missing one is a permanent state whenever the server-side theme fetch
 *   fails (nothing retries it on the client).
 * - Reads the value's shape rather than assuming base64, exactly as {@link resolveLogoSrc} does.
 *   Why: these are the same operator-uploaded fields, so a URL-shaped value is possible here too
 *   and would render as a broken mark on the brand fill.
 */
export function resolveBrandLogoSrc(
  logo: string | undefined | null,
  logoWhite: string | undefined | null
): string | undefined {
  const white = logoWhite?.trim() ?? '';
  if (white.length > 0) return toImageSrc(white);

  const standard = logo?.trim() ?? '';
  return standard.length > 0 ? toImageSrc(standard) : undefined;
}

const colorMappings: Array<[keyof Theme, string]> = [
  ['colorAccent', '--tw-color-accent'],
  ['colorBrandError', '--tw-color-error'],
  ['colorBrandWarn', '--tw-color-warn'],
  ['colorBrandWhite', '--tw-color-white'],
  ['colorGray', '--tw-color-gray'],
  ['colorGrayDark', '--tw-color-gray-dark'],
  ['colorGrayDarker', '--tw-color-gray-darker'],
  ['colorGrayLight', '--tw-color-gray-light'],
  ['colorGrayLighter', '--tw-color-gray-lighter'],
  ['colorBlack', '--tw-color-black'],
  ['colorFont', '--tw-color-font'],
  ['colorPrimary', '--tw-color-primary'],
  ['colorPrimaryDark', '--tw-color-primary-dark'],
  ['colorPrimaryDarker', '--tw-color-primary-darker'],
  ['colorPrimaryLight', '--tw-color-primary-light'],
  ['logoBackgroundColor', '--tw-color-logo-background'],
  ['whiteLabelHeaderColor', '--tw-color-whitelabel-header'],
  ['whiteLabelBackgroundTintColor', '--tw-color-whitelabel-background'],
];

/**
 * Generates CSS custom properties object from Theme for SSR or client-side application.
 * Returns an object suitable for React inline styles (e.g. { '--tw-color-primary': '#2c5082' }).
 *
 * @param theme - Theme object from API
 * @returns Object mapping CSS variable names to values, or null if theme is invalid
 */
export function generateThemeCSSVariables(
  theme: Theme | null | undefined
): Record<string, string> | null {
  if (!theme) return null;

  const styles: Record<string, string> = {};

  colorMappings.forEach(([themeKey, cssVar]) => {
    const value = theme[themeKey];
    if (value && typeof value === 'string') {
      styles[cssVar] = value;
      // Also set the un-prefixed variable (--color-*) used by @theme defaults.
      // Tailwind v4 with prefix(tw) may resolve utilities to either form
      // depending on build tooling, so both must be overridden at runtime.
      const unprefixed = cssVar.replace('--tw-color-', '--color-');
      if (unprefixed !== cssVar) {
        styles[unprefixed] = value;
      }
    }
  });

  return Object.keys(styles).length > 0 ? styles : null;
}

/**
 * Dark-mode-aware wrapper around generateThemeCSSVariables.
 * When isDarkMode is true, transforms the light theme through the HSL engine
 * before generating CSS variables.
 */
export function generateThemeCSSVariablesWithMode(
  theme: Theme | null | undefined,
  isDarkMode: boolean
): Record<string, string> | null {
  if (!theme) return null;
  const effectiveTheme = isDarkMode ? generateDarkTheme(theme) : theme;
  return generateThemeCSSVariables(effectiveTheme);
}

// ---------------------------------------------------------------------------
// Tab label + booking-mode helpers (multi-tenant branding)
// ---------------------------------------------------------------------------

/**
 * Resolves the i18n key for the Hourly tab label given the tenant's
 * `theme.hourlyTitleConfiguration`. Falls back to "hourly".
 */
export function resolveHourlyTabLabelKey(
  config: ThemeHourlyTitleConfigurationEnum | undefined | null
): 'hourly' | 'daily' {
  return config === ThemeHourlyTitleConfigurationEnum.DAILY ? 'daily' : 'hourly';
}

/**
 * Resolves the i18n key for the Monthly tab label given the tenant's
 * `theme.monthlyTitleConfiguration`. Falls back to "monthly".
 */
export function resolveMonthlyTabLabelKey(
  config: ThemeMonthlyTitleConfigurationEnum | undefined | null
): 'monthly' | 'permit' | 'pass' | 'tag' {
  switch (config) {
    case ThemeMonthlyTitleConfigurationEnum.PERMIT:
      return 'permit';
    case ThemeMonthlyTitleConfigurationEnum.PASS:
      return 'pass';
    case ThemeMonthlyTitleConfigurationEnum.TAG:
      return 'tag';
    default:
      return 'monthly';
  }
}

/**
 * Resolves the initial reservation mode for the search page when the URL
 * doesn't pin one. Honours `theme.defaultEcommerceBookingOption`; falls back
 * to hourly to preserve current behaviour for tenants that don't set it.
 */
export function resolveDefaultBookingMode(
  config: ThemeDefaultEcommerceBookingOptionEnum | undefined | null
): ReservationMode {
  return config === ThemeDefaultEcommerceBookingOptionEnum.MONTHLY
    ? ReservationMode.monthly
    : ReservationMode.hourly;
}

export const RESERVATION_SEARCH_LAYOUTS: readonly ReservationSearchLayout[] = [
  'BROWSE_BY_LOCATION',
  'BROWSE_BY_PRODUCT',
  'BROWSE_BY_PRODUCT_GROUPED',
  'COMPARE_PRODUCTS',
  // PB-5389. Adding it HERE is what makes `isProductLayout` return true for it (that predicate
  // is `!== 'BROWSE_BY_LOCATION'`, so it needs no change) and what stops the resolver degrading
  // the value away. The back office has its own copy of this array, and until the value is added
  // there too its `appliesToLayout` returns false and the Product Display Elements picker is
  // HIDDEN under exactly the layout that most needs it.
  'BROWSE_BY_CATEGORY',
  // PB-5390. Same reasoning, one ticket later. The back-office copy already carries this value —
  // `vm-reporting-website/src/components/company-settings/reservationSearchLayout.ts`, added by
  // PB 5388 — so the Product Display Elements picker is reachable under this layout from the
  // moment the api serves it. Stated with the file named because the previous phrasing asserted it
  // as settled fact with nothing to check it against, and a guarantee nobody can verify is
  // believed precisely because it was written down. Note it is NOT in that repo's `develop` as of
  // an old local checkout — verify against `origin/develop`, not a stale working copy.
  'BROWSE_BY_CATEGORY_TILES',
];

/** True for every layout that replaces the garage list with a product list and hides the map. */
export function isProductLayout(layout: ReservationSearchLayout): boolean {
  return layout !== 'BROWSE_BY_LOCATION';
}

/**
 * Resolves the company's reservation search layout from the raw `Theme` value.
 *
 * Takes the raw value rather than the whole theme, matching `resolveDefaultBookingMode` above
 * and keeping the unrecognised-value path directly testable.
 *
 * ## Invariants:
 * - Absent, null and unrecognised ALL resolve to `BROWSE_BY_LOCATION`, never throw, and never
 *   return null. A later build may write a value this one does not know; degrading to today's
 *   behaviour is the only safe reading, because a blank results column reads to a customer as a
 *   broken site. The unrecognised case is logged so the silent degrade is still observable.
 *
 * @param value raw `theme.reservationSearchLayout`, which may be absent or unknown to this build
 * @returns a layout this build understands; never null
 */
/**
 * Values already warned about, so an unrecognised layout logs once per value rather than once
 * per render. The resolver is called from two render bodies (the reserve page and
 * `useSearchState`), neither memoised, on a hook that re-renders on every keystroke — so the
 * unrecognised case, which is a certainty the first time the api ships a layout ahead of a
 * frontend deploy, would otherwise flood the console. The signal is worth keeping; the repeat
 * is not.
 */
const warnedLayouts = new Set<string>();

export function resolveReservationSearchLayout(
  value: string | undefined | null
): ReservationSearchLayout {
  if (value == null || value === '') return 'BROWSE_BY_LOCATION';

  if ((RESERVATION_SEARCH_LAYOUTS as readonly string[]).includes(value)) {
    return value as ReservationSearchLayout;
  }

  if (!warnedLayouts.has(value)) {
    warnedLayouts.add(value);
    logger.warn('Unrecognised reservationSearchLayout; falling back to BROWSE_BY_LOCATION', {
      feature: 'reservation-search',
      route: 'resolveReservationSearchLayout',
      value,
    });
  }
  return 'BROWSE_BY_LOCATION';
}

/**
 * Reads the layout off a `Theme`.
 *
 * The field is absent from the generated `Theme` type until the client package republishes, so
 * the read is bridged through a narrow cast rather than a module augmentation — per the repo
 * rule against overriding `@parkingbase` types.
 */
export function readReservationSearchLayout(
  theme: Theme | null | undefined
): ReservationSearchLayout {
  const raw = (theme as { reservationSearchLayout?: string } | null | undefined)
    ?.reservationSearchLayout;
  return resolveReservationSearchLayout(raw);
}

/**
 * The nine elements, in the api's declaration order.
 *
 * Declaration order decides nothing about rendering — it exists so an unrecognised value can be
 * told from a recognised one. `DEFAULT_PRODUCT_DISPLAY_ELEMENTS` below owns the render default.
 */
export const PRODUCT_DISPLAY_ELEMENTS: readonly ProductDisplayElement[] = [
  'PRODUCT',
  'DATES',
  'TYPE',
  'LOCATION_NAME',
  'DISTANCE',
  'DESCRIPTION',
  'PRICE',
  'ACCESS',
  'ADDRESS',
];

/**
 * What a company that has configured nothing renders, in order.
 *
 * ## Invariants:
 * - Must equal `Constants.ProductDisplayElement.DEFAULT_ORDER` in the api, which reproduces the
 *   screen exactly as it shipped so every existing company — all of which predate the field — is
 *   unaffected. If the two ever disagree, back office shows an operator a default this portal
 *   does not serve.
 * - ACCESS and ADDRESS are deliberately absent: they are the two opt-in elements.
 * - PB-5373 CORRECTED this order, in the api and here together. As PB-5371 shipped it, DATES sat
 *   SECOND while both surfaces have always rendered the window in the description slot, and
 *   nothing tested the claim that the default reproduced the screen. The order below is the one
 *   the compare table actually ships; the card's type badge moves below the product name to
 *   match it, which is the one deliberate visual change this ticket makes to the default.
 */
export const DEFAULT_PRODUCT_DISPLAY_ELEMENTS: readonly ProductDisplayElement[] = [
  'PRODUCT',
  'TYPE',
  'LOCATION_NAME',
  'DISTANCE',
  'DATES',
  'DESCRIPTION',
  'PRICE',
];

/**
 * Values already warned about, so an unrecognised element logs once per value rather than once
 * per render — same reasoning, and the same failure mode, as `warnedLayouts` above.
 */
const warnedElements = new Set<string>();

/**
 * Normalises the raw `productDisplayElements` value into an order this build can render.
 *
 * Mirrors `Constants.ProductDisplayElement.sanitize` in the api deliberately, rather than trusting
 * it. The api getter already sanitizes on read, so in practice the wire value arrives normalised —
 * but `Theme` is a wire model this portal does not control, and the one thing a product row must
 * never do is render blank. Reproducing the rules here costs a few lines and removes the
 * dependency on that guarantee holding.
 *
 * ## Invariants:
 * - Absent, null, non-array, empty, and a list whose every entry is unrecognised ALL resolve to
 *   `DEFAULT_PRODUCT_DISPLAY_ELEMENTS`. Never null, never empty — an empty product row is not a
 *   configuration anyone chose, it is the shape corruption takes.
 * - This is the OPPOSITE of the back-office picker's read path (PB-5372), which deliberately does
 *   NOT apply the empty-to-default fallback so that an operator's all-off state stays visible and
 *   its save can be refused against what is on screen. The divergence is intentional: back office
 *   must be able to represent a state this portal must never render.
 * - Unrecognised entries are DROPPED, never defaulted. Substituting a default into a list would
 *   duplicate an element already in it.
 * - Duplicates collapse to their FIRST occurrence. A region rendered twice is a defect however it
 *   got into the list.
 * - Matching is case-insensitive, matching the api's `fromName`.
 *
 * @param raw the stored value, which may be absent, null, or contain names unknown to this build
 * @returns a non-empty ordered element list; never null
 */
export function resolveProductDisplayElements(raw: unknown): readonly ProductDisplayElement[] {
  if (!Array.isArray(raw)) return DEFAULT_PRODUCT_DISPLAY_ELEMENTS;

  const canonical = new Map<string, ProductDisplayElement>(
    PRODUCT_DISPLAY_ELEMENTS.map((el) => [el.toLowerCase(), el])
  );
  const seen = new Set<ProductDisplayElement>();
  const order: ProductDisplayElement[] = [];

  raw.forEach((entry) => {
    if (typeof entry !== 'string' || entry === '') return;
    const element = canonical.get(entry.toLowerCase());
    if (!element) {
      if (!warnedElements.has(entry)) {
        warnedElements.add(entry);
        logger.warn('Unrecognised productDisplayElement; dropping it from the render order', {
          feature: 'reservation-search',
          route: 'resolveProductDisplayElements',
          value: entry,
        });
      }
      return;
    }
    if (seen.has(element)) return;
    seen.add(element);
    order.push(element);
  });

  return order.length > 0 ? order : DEFAULT_PRODUCT_DISPLAY_ELEMENTS;
}

/**
 * Layouts whose own headings state NO location, so every product row must carry its own.
 *
 * ## Invariants:
 * - Spelled out as an INCLUSION list, deliberately unlike `appliesToLayout` next door in the
 *   back office, which derives itself by naming the one excluded layout. That shape is right
 *   there (the setting applies everywhere except one place) and wrong here: a layout added
 *   later does NOT require a location line by default, and inheriting the requirement silently
 *   would force an element onto a surface nobody checked. Adding a category-style layout means
 *   adding it here on purpose.
 * - BOTH values now EXIST — PB-5389 added `BROWSE_BY_CATEGORY` and PB-5390 adds
 *   `BROWSE_BY_CATEGORY_TILES`, so the resolver returns each unchanged and degrades neither.
 *   The read below nonetheless still matches the RAW theme value, and that is deliberate rather
 *   than leftover: matching raw is what made this rule fire during the window when the api served
 *   a value this build did not know, and it costs nothing now that both are known. The note is
 *   kept because a future third layout re-opens exactly that window — the next person to add one
 *   should find the reason here rather than rediscover it.
 */
export const LOCATION_LINE_REQUIRED_LAYOUTS: readonly string[] = [
  'BROWSE_BY_CATEGORY',
  'BROWSE_BY_CATEGORY_TILES',
];

/** Whether this layout's product rows must each carry their own location line. */
export function requiresProductLocationLine(layout: string | null | undefined): boolean {
  return typeof layout === 'string' && LOCATION_LINE_REQUIRED_LAYOUTS.includes(layout);
}

/**
 * Forces the elements a LAYOUT requires on, over the operator's configuration.
 *
 * The category layouts group by category, so a heading names no place. A card there showing a
 * price with no location is not a terse card, it is an unanswerable one — the customer cannot
 * tell which garage they would be buying at. PB 5389 and PB 5390 both state the requirement and
 * both assign the fix here rather than working around it in their own render paths, which is
 * the right call: one forced-on rule beats two layouts each remembering to re-add a region.
 *
 * ## Invariants:
 * - This OVERRIDES the operator. That is the point, and it is the only place in this feature
 *   where the configuration is not obeyed verbatim — so it is narrow on purpose: one element,
 *   two layouts, and only when the element is absent. The back office refuses the same
 *   combination at save time, so an operator normally never reaches this state; this is the
 *   backstop for a configuration saved BEFORE the layout was switched, which no UI gate can
 *   catch. UI gating is not a boundary.
 * - Inserted at the slot `LOCATION_NAME` holds in the default order rather than appended, so a
 *   forced card reads like the default one. Clamped to the list length for a short list.
 * - Does NOT force `DISTANCE`. The requirement is that a row states its location; the garage
 *   name alone satisfies it, and forcing a second element would be inventing scope.
 */
/** Warn once per layout, matching `warnedLayouts` / `warnedElements` above. */
const warnedForcedLocation = new Set<string>();

export function applyLayoutElementRequirements(
  elements: readonly ProductDisplayElement[],
  rawLayout: string | null | undefined
): readonly ProductDisplayElement[] {
  if (!requiresProductLocationLine(rawLayout)) return elements;
  if (elements.includes('LOCATION_NAME')) return elements;

  // Say so. Overriding an operator's saved configuration SILENTLY is the failure mode this whole
  // feature keeps circling: the operator switched Location Name off, the save succeeded, and the
  // portal quietly does something else - so the only evidence is a card that disagrees with the
  // setting screen, which reads as a bug in the setting rather than a rule doing its job. The back
  // office refuses this combination at save time, so reaching here means a configuration that
  // predates the layout switch, and that is worth a line in the log.
  if (!warnedForcedLocation.has(rawLayout as string)) {
    warnedForcedLocation.add(rawLayout as string);
    logger.warn('Forcing LOCATION_NAME on: this layout groups by category and states no place', {
      feature: 'reservation-search',
      route: 'applyLayoutElementRequirements',
      value: rawLayout as string,
    });
  }

  const slot = Math.min(DEFAULT_PRODUCT_DISPLAY_ELEMENTS.indexOf('LOCATION_NAME'), elements.length);
  const forced = [...elements];
  forced.splice(slot, 0, 'LOCATION_NAME');
  return forced;
}

/**
 * Reads the configured element order off a `Theme`.
 *
 * The field is absent from the generated `Theme` type until the client package republishes, so the
 * read is bridged through a narrow cast rather than a module augmentation — per the repo rule
 * against overriding `@parkingbase` types. Same bridge, and the same removal condition, as
 * `readReservationSearchLayout` directly above: DELETE BOTH TOGETHER.
 */
export function readProductDisplayElements(
  theme: Theme | null | undefined
): readonly ProductDisplayElement[] {
  const bridged = theme as
    | { productDisplayElements?: unknown; reservationSearchLayout?: string }
    | null
    | undefined;
  // The RAW layout string, NOT `readReservationSearchLayout(theme)`. The resolver degrades a
  // value this build does not know to BROWSE_BY_LOCATION — and when this was written, the two
  // layouts needing the rule were exactly the values it did not know, so resolving first would
  // have made the requirement permanently inert the moment the api began serving them.
  //
  // BOTH values are known now (PB 5389 and PB 5390 added them), so the two reads agree for
  // them today. The raw read stays anyway, and not out of inertia: a future third category
  // layout re-opens exactly that window, and this is the line that would have to change back.
  // It is harmless in the meantime — under BROWSE_BY_LOCATION no product surface renders at
  // all, so a forced element changes nothing.
  return applyLayoutElementRequirements(
    resolveProductDisplayElements(bridged?.productDisplayElements),
    bridged?.reservationSearchLayout
  );
}

/**
 * Reads the rate card label taxonomy off a `Theme` (PB-5389).
 *
 * The field is absent from the generated `Theme` type until the client package republishes, so
 * the read is bridged through a narrow cast rather than a module augmentation — per the repo
 * rule against overriding `@parkingbase` types. FOURTH bridge of this shape in this file's
 * neighbourhood (layout, display elements, the category layouts' location requirement, and now
 * this); DELETE THEM ALL TOGETHER when the package regenerates.
 *
 * ## Invariants:
 * - Returns an array, never null, so every caller can map without a guard. An absent taxonomy
 *   and an empty one mean the same thing here — no categories — and `buildCategoryTree`
 *   degrades identically for both.
 * - Does NOT filter or validate. Partitioning visible from hidden, and HEADING from FILTER, is
 *   `buildCategoryTree`'s job and lives in exactly one place; doing any of it here would put
 *   the same rules in two files.
 */
export function readRateCardLabelDimensions(
  theme: Theme | null | undefined
): RateCardLabelDimension[] {
  const raw = (theme as { rateCardLabelDimensions?: unknown } | null | undefined)
    ?.rateCardLabelDimensions;
  return Array.isArray(raw) ? (raw as RateCardLabelDimension[]) : [];
}

/**
 * Reads a rate's label tags off an ecommerce search result (PB-5389).
 *
 * The SIXTH and last member of the bridge family in this file, and it is removed with the
 * others when the published client catches up - see the shared removal condition above.
 * `PermitRateResult` and `HourlyRateResult` gain `tags` in PB-5391; until that api change is
 * published in `@parkingbase/ecommerce-api-demo`, the field is invisible to the compiler.
 *
 * ## Invariants:
 * - ABSENT and EMPTY are deliberately collapsed to `undefined` here, and the tree builder
 *   distinguishes them nowhere either: an untagged product resolves to no named category by
 *   the same rule whether the operator tagged nothing or the api sent nothing, and lands on the
 *   uncategorised fallback node (PB-5579). The place that
 *   distinction IS load-bearing is the degrade check - a taxonomy that placed no product at
 *   all degrades to the flat list rather than emptying the booking site - and that check reads
 *   the whole result set, not one rate.
 * - Returns a NEW array never a live reference into the response, because the tree builder
 *   sorts and partitions what it is handed.
 */
export function readRateCardTags(rate: unknown): RateCardTag[] | undefined {
  const raw = (rate as { tags?: unknown } | null | undefined)?.tags;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  // A COPY, as the invariant above promises. Returning the response's own array made that
  // sentence false - harmless today, and exactly the kind of wrong note that gets believed: the
  // next caller reads it, sorts or splices in place, and mutates the cached search response.
  return [...raw] as RateCardTag[];
}

/**
 * Company-level booking enablement resolved from the back-office toggles
 * (`theme.hourlyEnabled` / `theme.monthlyEnabled`, mapped from Company by the
 * API's `Theme.setCompanyValues()`).
 */
export interface CompanyBookingEnablement {
  hourly: boolean;
  monthly: boolean;
}

/**
 * Resolves the company-level hourly/monthly booking toggles from the theme.
 *
 * ## Invariants:
 * - A null/undefined theme means "not hydrated yet" and must NOT gate anything —
 *   both modes report enabled until the real flags arrive. Callers re-resolve
 *   once the theme loads.
 * - A loaded theme with an unset flag counts as disabled (`!!flag`), matching
 *   legacy `isHourlyEnabled()`/`isMonthlyEnabled()` in GarageListPage.
 */
export function getCompanyBookingEnablement(
  theme: Theme | null | undefined
): CompanyBookingEnablement {
  if (!theme) return { hourly: true, monthly: true };
  return { hourly: !!theme.hourlyEnabled, monthly: !!theme.monthlyEnabled };
}

/**
 * Whether the Hourly/Monthly mode switcher should render. Legacy parity: the
 * switcher only exists when BOTH company-level toggles are on; with one off the
 * customer is locked to the enabled mode (legacy GarageListPage renders the
 * tablist under `isMonthlyEnabled() && isHourlyEnabled()`).
 */
export function isModeToggleVisible(theme: Theme | null | undefined): boolean {
  // PB-5345: a product layout replaces the Hourly/Monthly dimension with type filter chips, so
  // the toggle is suppressed rather than restyled. Folded into the shared predicate deliberately
  // — this rule has FOUR call sites (the reserve search bar, the mobile filter sheet, and the
  // landing hero twice), and gating them individually already missed two. A fifth consumer now
  // inherits it rather than having to remember.
  if (isProductLayout(readReservationSearchLayout(theme))) return false;
  const enablement = getCompanyBookingEnablement(theme);
  return enablement.hourly && enablement.monthly;
}

/**
 * Clamps a requested reservation mode to the company-level toggles.
 *
 * ## Invariants:
 * - The company toggle overrides every mode source, including a URL-pinned
 *   `?mode=` — legacy computes `isMonthly = isMonthlyEnabled() && (…)`, so a
 *   monthly deep-link on a monthly-off company still lands on hourly.
 * - Both toggles off falls back to hourly (legacy fail-open: `isMonthly`
 *   resolves false and the hourly view renders).
 */
export function clampModeToCompanyEnablement(
  theme: Theme | null | undefined,
  mode: ReservationMode
): ReservationMode {
  const enablement = getCompanyBookingEnablement(theme);
  if (mode === ReservationMode.monthly) {
    return enablement.monthly ? ReservationMode.monthly : ReservationMode.hourly;
  }
  return enablement.hourly || !enablement.monthly
    ? ReservationMode.hourly
    : ReservationMode.monthly;
}

/**
 * Resolves the parking-pass display title for the tenant. Back-office stores
 * an arbitrary string (`theme.parkingPassTitleConfiguration`) which may come
 * back in any casing; we normalize to Title Case so the rendered tab heading
 * looks consistent. Returns the fallback (typically a translated default)
 * when the field is empty.
 */
export function resolveParkingPassTitle(raw: string | undefined | null, fallback: string): string {
  const trimmed = raw?.trim();
  if (!trimmed) return fallback;
  return trimmed.replace(
    /\w\S*/g,
    (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
}

/**
 * Pluralizes the (already resolved) parking-pass title for user-visible copy.
 *
 * ## Invariants:
 * - Exactly one trailing "s": a title the operator already typed as a plural
 *   ("Visitor Permits") is returned unchanged, never "Permitses".
 * - The suffix is appended in lowercase; the rest of the title keeps its casing.
 */
export function pluralizeParkingPassTitle(title: string): string {
  if (!title) return title;
  const lower = title.toLowerCase();
  if (/(ss|x|z|ch|sh)$/.test(lower)) return `${title}es`;
  if (/[^aeiou]y$/.test(lower)) return `${title.slice(0, -1)}ies`;
  if (lower.endsWith('s')) return title;
  return `${title}s`;
}

/**
 * Resolves the Find-Parking CTA copy for the tenant. Back-office stores an
 * arbitrary string in `theme.findParkingLabel`; we trim and pass it through.
 * Returns the fallback (typically a translated default) when the field is empty.
 */
// Legacy renders Pay by Plate as a home-page tab listed in this array, defaulting
// to Find Parking only (HomePage.tsx:970-980). It is a company config — not a
// permission, so it must not be conflated with special-rate access.
export function isPayByPlateEnabled(theme: Theme | null | undefined): boolean {
  return (
    theme?.reservationHomeSearchScreen?.includes(
      ThemeReservationHomeSearchScreenEnum.PAY_BY_PLATE
    ) ?? false
  );
}

/**
 * Whether the company wants customers to land on the booking screen rather than the landing page
 * (PB-5360).
 *
 * ## Invariants:
 * - Read from `Theme`, never `Company`. This portal receives feature flags via `setCompanyValues()`
 *   on the api's Theme model; reading Company here would work in back office and silently do
 *   nothing in production, which is the defect this ticket exists to fix — just one layer lower.
 * - Absent and `false` are identical. Every existing company predates this field reaching the new
 *   portal, so absent is the common case and must behave exactly as today.
 */
export function isDefaultToBookingScreenEnabled(theme: Theme | null | undefined): boolean {
  return theme?.defaultToBookingScreen === true;
}

/**
 * Whether the company has opted into the PB-235 (Rutgers CR-048) daily-reservation block: hide the
 * daily/hourly booking option for active-permit holders (FR-1) and check the entered plate against
 * active-permit / outstanding-violation records (FR-2/FR-3) before payment.
 *
 * ## Invariants:
 * - Read through a narrow cast because the field is added on the API ahead of the generated Theme
 *   type; absent/false means the feature is off and the daily flow behaves exactly as today.
 */
export function isDailyReservationBlockEnabled(theme: Theme | null | undefined): boolean {
  return (
    (theme as { blockDailyReservationForPermitOrViolation?: boolean } | null | undefined)
      ?.blockDailyReservationForPermitOrViolation === true
  );
}

/**
 * The destination the operator configured to pre-seed the booking search (PB-5360).
 *
 * ## Invariants:
 * - Empty string and whitespace read as UNSET, not as a search for nothing. An operator who
 *   clears the field expects the geolocation path, not a geocode of "".
 */
export function readDefaultBookingSearchText(theme: Theme | null | undefined): string {
  const raw = theme?.defaultBookingScreenSearchText;
  return typeof raw === 'string' ? raw.trim() : '';
}

export function resolveFindParkingLabel(raw: string | undefined | null, fallback: string): string {
  const trimmed = raw?.trim();
  return trimmed || fallback;
}

// ---------------------------------------------------------------------------
// Date validation policy helpers (legacy theme constraints)
// ---------------------------------------------------------------------------

/**
 * Monthly permit start date, mirroring the legacy site: `startDate` is the value
 * to pre-select, `minDate` the earliest selectable — they differ. minDate stays
 * today for every theme case so current-month dates remain selectable; only a
 * future employment/hire date raises it. (PB-2239)
 */
export function getPermitBookingStartDate(
  theme: Theme | null | undefined,
  userProfile: UserProfile | null | undefined,
  now: Date
): { startDate: Date; minDate: Date } {
  const today = startOfDay(now);
  const companyId = theme?.companyId;
  const customAttributes = companyId ? userProfile?.customAttributes?.[companyId] : undefined;

  const employeeTypeCode = customAttributes?.['EMPLOYEE_TYPE_CODE'];
  const skipEmployeeDates = employeeTypeCode != null && Number(employeeTypeCode) === 5;

  if (!skipEmployeeDates && customAttributes) {
    const employeeDate = customAttributes['EMPLOYMENT_BEGIN_DATE'] || customAttributes['Hire Date'];
    if (employeeDate) {
      const parsed = parseFlexibleDate(employeeDate, PERMIT_DATE_FORMATS, now);
      const date = parsed && isAfter(startOfDay(parsed), today) ? startOfDay(parsed) : today;
      return { startDate: date, minDate: date };
    }
  }

  switch (theme?.permitBookingStartDate) {
    case ThemePermitBookingStartDateEnum.CURRENT_DAY:
      return { startDate: today, minDate: today };

    case ThemePermitBookingStartDateEnum.CUSTOM_DATE: {
      const raw = theme?.customPermitBookingStartDate;
      const parsed = raw ? parseISO(raw) : null;
      const custom = parsed && isValid(parsed) ? startOfDay(parsed) : null;
      const selected = custom && !isBefore(custom, today) ? custom : today;
      return { startDate: selected, minDate: today };
    }

    default:
      return { startDate: startOfMonth(addMonths(now, 1)), minDate: today };
  }
}

/**
 * Earliest selectable date (picker `minDate`). Hourly honours the
 * allowReservationInPast grace window; monthly delegates to
 * {@link getPermitBookingStartDate} (minDate = today for theme cases).
 */
export function getEarliestAllowedDate(
  theme: Theme | null | undefined,
  mode: ReservationMode | string,
  now: Date,
  userProfile?: UserProfile | null
): Date {
  if (!theme) return now;
  const isMonthly = mode === ReservationMode.monthly || mode === 'monthly';

  if (isMonthly) {
    return getPermitBookingStartDate(theme, userProfile, now).minDate;
  }

  // Hourly: optional past-booking grace window
  if (theme.allowReservationInPast === true) {
    const grace = theme.reservationPastBookingMinutes;
    if (typeof grace === 'number' && grace > 0) {
      return addMinutes(now, -grace);
    }
  }
  return now;
}

/**
 * Whether an hourly start (enter) instant may be picked — legacy `filterEnterAfterTime` parity.
 *
 * ## Invariants:
 * - `now` defaults to the wall clock at call time, never a value memoised at mount. Why: a tab
 *   left open past a slot (or past midnight) must stop offering it.
 */
export function isEnterInstantAllowed(
  theme: Theme | null | undefined,
  instant: Date,
  now: Date = new Date()
): boolean {
  return instant.getTime() >= getEarliestAllowedDate(theme, ReservationMode.hourly, now).getTime();
}

/**
 * Clamps a typed/keyboard hourly start up to the earliest allowed instant (rounded up to the
 * next 15-minute slot so it lands on a picker slot). An allowed start is returned unchanged.
 */
export function clampEnterToEarliestAllowed(
  theme: Theme | null | undefined,
  instant: Date,
  now: Date = new Date()
): Date {
  if (isEnterInstantAllowed(theme, instant, now)) return instant;
  return roundToNext15Minutes(getEarliestAllowedDate(theme, ReservationMode.hourly, now));
}

/** Default start date to pre-select: monthly uses the configured permit start; hourly uses `now`. */
export function getDefaultStartDate(
  theme: Theme | null | undefined,
  mode: ReservationMode | string,
  now: Date,
  userProfile?: UserProfile | null
): Date {
  if (!theme) return now;
  const isMonthly = mode === ReservationMode.monthly || mode === 'monthly';
  if (!isMonthly) return now;
  return getPermitBookingStartDate(theme, userProfile, now).startDate;
}

// The 180 fallback is load-bearing, not arbitrary: deep-linked special rates are
// priced cheapest-rate-wins per window, so a shorter default can make a flat
// special rate lose to the hourly board rate. Matches legacy's `|| 180`.
export function getDefaultDurationMinutes(theme: Theme | null | undefined): number {
  const mins = theme?.ecommerceHourlyDefaultDurationInMins;
  return typeof mins === 'number' && mins > 0 ? mins : DEFAULT_DURATION_MINUTES;
}

/**
 * Latest start date a customer can pick — caps at
 * `now + theme.allowedFutureDateParking` days. Falls back to a one-year
 * window when the field isn't set, matching the prior hard-coded default.
 */
export function getLatestAllowedDate(theme: Theme | null | undefined, now: Date): Date {
  const days =
    typeof theme?.allowedFutureDateParking === 'number' && theme.allowedFutureDateParking > 0
      ? theme.allowedFutureDateParking
      : DEFAULT_FUTURE_BOOKING_DAYS;
  return addDays(now, days);
}

// ---------------------------------------------------------------------------
// Customer support contact (Theme-level fallback)
// ---------------------------------------------------------------------------

export interface SupportContactDisplay {
  name?: string;
  phoneNumbers: string[];
  emailAddresses: string[];
}

interface SupportContactSource {
  customerSupportInfo?: {
    name?: string;
    phoneNumbers?: string[];
    emailAddresses?: string[];
  } | null;
}

interface ThemeContactLike {
  reservationContact?: {
    firstName?: string;
    lastName?: string;
    phoneNumbers?: { number?: string }[] | null;
    emailAddresses?: string[] | null;
  } | null;
}

/**
 * Resolves the support contact to render when a garage is unbookable.
 * Garage-level `customerSupportInfo` (already a flat DTO from PB-1083) wins;
 * falls back to `theme.reservationContact` when the garage didn't configure
 * one. Returns null if neither side has anything useful.
 */
export function resolveSupportContact(
  garage: SupportContactSource | null | undefined,
  theme: ThemeContactLike | null | undefined
): SupportContactDisplay | null {
  const garageInfo = garage?.customerSupportInfo;
  if (
    garageInfo &&
    (garageInfo.name ||
      (garageInfo.phoneNumbers?.length ?? 0) > 0 ||
      (garageInfo.emailAddresses?.length ?? 0) > 0)
  ) {
    return {
      name: garageInfo.name?.trim() || undefined,
      phoneNumbers: (garageInfo.phoneNumbers ?? []).filter(Boolean),
      emailAddresses: (garageInfo.emailAddresses ?? []).filter(Boolean),
    };
  }

  const tc = theme?.reservationContact;
  if (!tc) return null;
  const namePieces = [tc.firstName, tc.lastName].filter((p): p is string => Boolean(p?.trim()));
  const name = namePieces.length > 0 ? namePieces.join(' ') : undefined;
  const phones = (tc.phoneNumbers ?? [])
    .map((p) => (typeof p === 'string' ? p : p?.number))
    .filter((p): p is string => Boolean(p && p.trim()));
  const emails = (tc.emailAddresses ?? []).filter((e): e is string => Boolean(e && e.trim()));

  if (!name && phones.length === 0 && emails.length === 0) return null;
  return { name, phoneNumbers: phones, emailAddresses: emails };
}

/**
 * Alpha-2 country codes place suggestions may be drawn from, per company settings.
 * Falls back to US + Canada when the company has no configured list.
 */
export function resolveSearchableCountries(theme: Theme | null | undefined): string[] {
  const configured = theme?.mapsSearchableCountries;
  if (!configured?.length) return DEFAULT_SEARCHABLE_COUNTRIES;
  const codes = Array.from(new Set(configured.filter(Boolean).map(countryToAlphaCode)));
  return codes.length > 0 ? codes : DEFAULT_SEARCHABLE_COUNTRIES;
}
